import React, {useEffect} from 'react';
import {connect} from "react-redux";
import {withTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import Footer from "../core/layout/footer/footer";
import '../assets/css/home.css';

const Home = (props) => {
    useEffect(() => {
        window.scrollTo(0, 0);

        const io = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('visible');
                    io.unobserve(e.target);
                }
            });
        }, {threshold: 0.08});

        document.querySelectorAll('.dl-fade-up').forEach(el => io.observe(el));

        return () => io.disconnect();
    }, []);

    return (
        <div className="w-100">

            {/* ── HERO ─────────────────────────────── */}
            <section className="dl-hero">
                <div className="dl-hero-inner">
                    <div>
                        <div className="dl-hero-eyebrow">
                            <i className="fas fa-bolt"></i> Ideas to Reality — Together
                        </div>
                        <h1>
                            Your business challenge.<br/>
                            Our technology.<br/>
                            <span className="blue">Built to ship.</span>
                        </h1>
                        <p className="dl-hero-desc">
                            We partner with startups and mid-sized businesses to build AI-powered workflows
                            and custom software — delivered by senior engineers who stay with you from first
                            line of code to production.
                        </p>
                        <div className="dl-hero-actions">
                            <a
                                href="#contact"
                                className="dl-btn dl-btn-blue"
                                onClick={e => {
                                    e.preventDefault();
                                    document.getElementById('contact')?.scrollIntoView({behavior: 'smooth'});
                                }}
                            >
                                <i className="fas fa-rocket"></i> Let's Build Together
                            </a>
                            <Link to="/services/ai-capabilities" className="dl-btn dl-btn-outline">
                                <i className="fas fa-microchip"></i> Explore AI Capabilities
                            </Link>
                        </div>
                    </div>
                    <div className="dl-hero-right">
                        <div className="dl-hero-card">
                            <div className="dl-hc-icon"><i className="fas fa-robot"></i></div>
                            <div className="dl-hc-title">Agentic AI</div>
                            <div className="dl-hc-desc">Autonomous agents that plan, execute tasks, and keep you in control at every step</div>
                        </div>
                        <div className="dl-hero-card">
                            <div className="dl-hc-icon"><i className="fas fa-layer-group"></i></div>
                            <div className="dl-hc-title">Multimodal AI</div>
                            <div className="dl-hc-desc">Text, vision, speech, and video — AI across every data type your business works with</div>
                        </div>
                        <div className="dl-hero-card">
                            <div className="dl-hc-icon"><i className="fas fa-laptop-code"></i></div>
                            <div className="dl-hc-title">Custom Software</div>
                            <div className="dl-hc-desc">Web, mobile, cloud — production-ready software built by senior engineers</div>
                        </div>
                        <div className="dl-hero-card">
                            <div className="dl-hc-icon"><i className="fas fa-bolt"></i></div>
                            <div className="dl-hc-title">Digital Accelerators</div>
                            <div className="dl-hc-desc">Pre-built, configurable components that cut delivery time and de-risk your build</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SERVICES ─────────────────────────── */}
            <section className="dl-services">
                <div className="dl-wrap">
                    <div className="dl-s-header dl-fade-up">
                        <div className="dl-section-tag">What We Do</div>
                        <h2 className="dl-s-title">Technology that <span>earns its place</span></h2>
                        <p className="dl-s-sub">
                            We build across two complementary capabilities — AI-powered solutions that automate
                            and augment, and custom software that scales.
                        </p>
                    </div>

                    <div className="dl-services-grid">

                        {/* AI — featured full-width card */}
                        <div className="dl-svc-card ai-card dl-fade-up">
                            <div>
                                <div className="dl-svc-icon"><i className="fas fa-microchip"></i></div>
                                <h3 className="ai-h">AI Capabilities</h3>
                                <p className="ai-p">
                                    Production-tested AI across five capability pillars — for startups and mid-sized
                                    businesses who are ready to make AI work, not just look impressive on a slide.
                                </p>
                                <div className="dl-ai-caps">
                                    <span className="dl-ai-cap"><i className="fas fa-align-left"></i> Text &amp; LLM</span>
                                    <span className="dl-ai-cap"><i className="fas fa-eye"></i> Vision &amp; Docs</span>
                                    <span className="dl-ai-cap"><i className="fas fa-microphone-alt"></i> Speech &amp; Voice</span>
                                    <span className="dl-ai-cap"><i className="fas fa-video"></i> Video Intelligence</span>
                                    <span className="dl-ai-cap"><i className="fas fa-robot"></i> Agentic AI</span>
                                </div>
                                <Link to="/services/ai-capabilities" className="dl-svc-link">
                                    Explore AI Capabilities <i className="fas fa-arrow-right"></i>
                                </Link>
                            </div>
                            <div className="dl-ai-card-right">
                                <div className="dl-ai-card-steps">
                                    <div className="dl-ai-step">
                                        <span className="dl-ai-step-num">01</span>
                                        <div>
                                            <div className="dl-ai-step-text">Problem-first, always</div>
                                            <div className="dl-ai-step-sub">Your challenge before any technology suggestion</div>
                                        </div>
                                    </div>
                                    <div className="dl-ai-step">
                                        <span className="dl-ai-step-num">02</span>
                                        <div>
                                            <div className="dl-ai-step-text">Honest about what AI can do</div>
                                            <div className="dl-ai-step-sub">If it's not the right tool, we'll say so</div>
                                        </div>
                                    </div>
                                    <div className="dl-ai-step">
                                        <span className="dl-ai-step-num">03</span>
                                        <div>
                                            <div className="dl-ai-step-text">Built for production, not demos</div>
                                            <div className="dl-ai-step-sub">Real data, real systems, real constraints</div>
                                        </div>
                                    </div>
                                    <div className="dl-ai-step">
                                        <span className="dl-ai-step-num">04</span>
                                        <div>
                                            <div className="dl-ai-step-text">We grow with you</div>
                                            <div className="dl-ai-step-sub">A long-term AI partner, not a one-off project</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Technology Partnership */}
                        <div className="dl-svc-card dl-fade-up d1">
                            <div className="dl-svc-icon"><i className="fas fa-handshake"></i></div>
                            <h3>Technology Partnership</h3>
                            <p>
                                For startups and businesses that need a trusted technology co-founder — strategic
                                guidance, architecture decisions, and hands-on engineering, together.
                            </p>
                            <Link to="/services/technology-partnership" className="dl-svc-link">
                                Learn more <i className="fas fa-arrow-right"></i>
                            </Link>
                        </div>

                        {/* Custom Software Development */}
                        <div className="dl-svc-card dl-fade-up d2">
                            <div className="dl-svc-icon"><i className="fas fa-laptop-code"></i></div>
                            <h3>Custom Software Development</h3>
                            <p>
                                Web, mobile, cloud, and data engineering — built from scratch or on top of your
                                existing stack. Fast delivery by senior engineers who understand business, not just
                                code.
                            </p>
                            <Link to="/services/custom-software-development" className="dl-svc-link">
                                Learn more <i className="fas fa-arrow-right"></i>
                            </Link>
                        </div>

                        {/* Digital Accelerators */}
                        <div className="dl-svc-card dl-fade-up d3">
                            <div className="dl-svc-icon"><i className="fas fa-bolt"></i></div>
                            <h3>Digital Accelerators</h3>
                            <p>
                                Pre-built, configurable components that cut delivery time and reduce risk — so you
                                spend less time building foundations and more time building what matters.
                            </p>
                            <Link to="/contact-us" className="dl-svc-link">
                                Learn more <i className="fas fa-arrow-right"></i>
                            </Link>
                        </div>

                    </div>
                </div>
            </section>

            {/* ── WHO WE WORK WITH ─────────────────── */}
            <section className="dl-who">
                <div className="dl-wrap">
                    <div className="dl-s-header dl-fade-up">
                        <div className="dl-section-tag">Who We Work With</div>
                        <h2 className="dl-s-title">Built for businesses <span>ready to move fast</span></h2>
                        <p className="dl-s-sub">
                            We work best with organisations that value speed, directness, and senior-level
                            engagement — where the decision-maker and the builder are in the same room.
                        </p>
                    </div>

                    <div className="dl-who-grid">

                        <div className="dl-who-card dl-fade-up">
                            <div className="dl-who-card-top">
                                <div className="dl-who-icon"><i className="fas fa-seedling"></i></div>
                                <h3>Startups</h3>
                            </div>
                            <p className="dl-who-desc">
                                You have an idea, early traction, or a product that needs to scale — and you need a
                                technology partner who moves at your pace, not a vendor who slows you down.
                            </p>
                            <ul className="dl-who-list">
                                <li><i className="fas fa-check"></i> First product build from zero</li>
                                <li><i className="fas fa-check"></i> AI embedded from day one</li>
                                <li><i className="fas fa-check"></i> Architecture that grows with you</li>
                                <li><i className="fas fa-check"></i> Senior engineers, no junior handoffs</li>
                            </ul>
                            <div className="dl-who-cta">
                                <Link to="/contact-us" className="dl-who-link">
                                    Talk to us about your idea <i className="fas fa-arrow-right"></i>
                                </Link>
                            </div>
                        </div>

                        <div className="dl-who-card dl-fade-up d2">
                            <div className="dl-who-card-top">
                                <div className="dl-who-icon"><i className="fas fa-chart-line"></i></div>
                                <h3>Growing Businesses</h3>
                            </div>
                            <p className="dl-who-desc">
                                You have a working product and a growing team — but manual processes, legacy systems,
                                or missed AI opportunities are slowing down what should be a faster operation.
                            </p>
                            <ul className="dl-who-list">
                                <li><i className="fas fa-check"></i> AI automation of manual workflows</li>
                                <li><i className="fas fa-check"></i> Modernisation of existing systems</li>
                                <li><i className="fas fa-check"></i> New capability without rebuilding everything</li>
                                <li><i className="fas fa-check"></i> Direct access to the engineers doing the work</li>
                            </ul>
                            <div className="dl-who-cta">
                                <Link to="/contact-us" className="dl-who-link">
                                    Tell us what's slowing you down <i className="fas fa-arrow-right"></i>
                                </Link>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ── OUR CAPABILITIES ─────────────────── */}
            <section className="dl-caps">
                <div className="dl-wrap">
                    <div className="dl-caps-layout">

                        <div className="dl-fade-up">
                            <h2 className="dl-caps-title">Our capabilities</h2>
                            <div className="dl-caps-quote">
                                <i className="fas fa-quote-left"></i>
                                Ideas are easy. Implementation is hard.
                                <i className="fas fa-quote-right"></i>
                                <span className="dl-caps-attribution">— Guy Kawasaki</span>
                            </div>
                            <p className="dl-caps-body">
                                <span className="dl-caps-accent">Its all about execution.</span> Facebook was not the
                                first social network, the iPhone was not the first smartphone, Google was not the
                                first search engine. Though the ideas were outstanding, it is coupled with superior
                                technology implementation.
                            </p>
                            <p className="dl-caps-body">
                                With our vast experience, we bring this{' '}
                                <span className="dl-caps-highlight-orange">flawless execution</span>{' '}
                                while bringing{' '}
                                <span className="dl-caps-highlight-blue">ideas to reality.</span>
                            </p>
                        </div>

                        <div className="dl-caps-grid dl-fade-up d1">
                            <div className="dl-cap-card">
                                <div className="dl-cap-icon"><i className="fas fa-microchip"></i></div>
                                <h3>Cutting-edge Technologies</h3>
                                <p>We have proven experience in developing high performing solutions in FinTech,
                                    data analytics, multi-device platforms, cloud, AI and ML.</p>
                            </div>
                            <div className="dl-cap-card">
                                <div className="dl-cap-icon"><i className="fas fa-sync-alt"></i></div>
                                <h3>Execution flexibility</h3>
                                <p>Change is the only constant in business life. Our proven delivery model is
                                    carefully designed to embrace changing business needs.</p>
                            </div>
                            <div className="dl-cap-card">
                                <div className="dl-cap-icon"><i className="fas fa-globe"></i></div>
                                <h3>IP assets</h3>
                                <p>We generate rich set of high quality IP assets that helps customers with
                                    significantly reduced go-to-market time at a fraction of cost.</p>
                            </div>
                            <div className="dl-cap-card">
                                <div className="dl-cap-icon"><i className="fas fa-user-tie"></i></div>
                                <h3>Deep technology expertise</h3>
                                <p>Both CEO and CTO are highly experienced professionals having worked at global
                                    corporates as well as startups with combined experience of more than 50 years.</p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ── VALUE PROPOSITION ────────────────── */}
            <section className="dl-value-prop">
                <div className="dl-wrap">
                    <div className="dl-vp-header dl-fade-up">
                        <h2 className="dl-vp-title">Value Proposition</h2>
                        <p className="dl-vp-sub">
                            We being partner with shared risks and rewards, we strive to make your business vision
                            a grand success.
                        </p>
                    </div>
                    <div className="dl-vp-grid dl-fade-up d1">
                        <div className="dl-vp-card">
                            <h3>Fast</h3>
                            <p>Home-grown IT assets to reduces go-to-market cycle dramatically.</p>
                        </div>
                        <div className="dl-vp-card">
                            <h3>Flexible</h3>
                            <p>Stretching the limits of IT architecture for the highest possible flexibility.</p>
                        </div>
                        <div className="dl-vp-card">
                            <h3>Future-Proof</h3>
                            <p>Latest cutting-edge technology that is ready to grow with your business growth.</p>
                        </div>
                        <div className="dl-vp-card">
                            <h3>Cost Effective</h3>
                            <p>Significantly lower cost of development as lean setup combined with partnership model.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA ──────────────────────────────── */}
            <section className="dl-cta" id="contact">
                <div className="dl-cta-inner">
                    <div className="dl-fade-up">
                        <h2>Ready to build something real?</h2>
                        <p>
                            Tell us about your business challenge. We'll tell you honestly whether technology can
                            solve it — and if it can, exactly how we'd do it.
                        </p>
                    </div>
                    <div className="dl-cta-actions dl-fade-up d2">
                        <Link to="/contact-us" className="dl-btn dl-btn-white">
                            <i className="fas fa-envelope"/> info@deccanlogic.com
                        </Link>
                    </div>
                </div>
            </section>

            <Footer/>

        </div>
    );
};

Home.propTypes = {};

Home.defaultProps = {};

const mapStateToProps = (state) => ({
    isMobileView: state.appState.deviceInfo.isMobileView,
    isLGView: state.appState.deviceInfo.isLGView,
    isXLView: state.appState.deviceInfo.isXLView,
    isXSView: state.appState.deviceInfo.isXSView,
    isSMView: state.appState.deviceInfo.isSMView,
});

export default connect(mapStateToProps, {})(withTranslation('translations')(Home));
